/**
 * StepperBar
 *
 * Numbered-circle progress indicator for the contact-import wizard.
 * - active step   → brand circle with a focus ring
 * - completed step → success circle with a check icon (clickable to navigate back)
 * - upcoming step  → muted outlined circle
 * Connector lines between steps fill with success as the user progresses.
 */
import { Check } from 'lucide-react'
import { cn } from 'components/UI/cn'
import { WizardStep } from '../wizardState'

export interface StepperStep {
  key: WizardStep
  label: string
}

export const STEPPER_STEPS: StepperStep[] = [
  { key: 'upload', label: 'Upload' },
  { key: 'map', label: 'Map Columns' },
  { key: 'validate', label: 'Validate' },
  { key: 'review', label: 'Review & Import' },
]

interface StepperBarProps {
  currentStep: WizardStep
  /** Optional handler invoked when a completed step is clicked. */
  onStepClick?: (step: WizardStep) => void
}

export function StepperBar({ currentStep, onStepClick }: StepperBarProps) {
  const currentIdx = STEPPER_STEPS.findIndex((s) => s.key === currentStep)

  return (
    <div className="flex items-center w-full">
      {STEPPER_STEPS.map((step, idx) => {
        const isComplete = idx < currentIdx
        const isActive = idx === currentIdx
        const isClickable = isComplete && onStepClick != null

        return (
          <div key={step.key} className="flex items-center flex-1 min-w-0">
            <button
              type="button"
              disabled={!isClickable}
              onClick={isClickable ? () => onStepClick(step.key) : undefined}
              className={cn(
                'flex items-center gap-2 shrink-0',
                isClickable ? 'cursor-pointer' : 'cursor-default'
              )}
            >
              {/* Circle */}
              <div
                className={cn(
                  'flex items-center justify-center rounded-full size-6 shrink-0 transition-all duration-200',
                  {
                    'bg-success text-white': isComplete,
                    'bg-brand text-white ring-4 ring-brand/20': isActive,
                    'bg-surface border-2 border-line text-fg-muted':
                      !isComplete && !isActive,
                  }
                )}
              >
                {isComplete ? (
                  <Check className="size-3" strokeWidth={3} />
                ) : (
                  <span className="text-xs font-bold leading-none">
                    {idx + 1}
                  </span>
                )}
              </div>

              {/* Label */}
              <span
                className={cn(
                  'text-sm whitespace-nowrap transition-colors duration-200',
                  {
                    'text-brand font-semibold': isActive,
                    'text-fg font-medium': isComplete,
                    'text-fg-muted font-normal': !isActive && !isComplete,
                  }
                )}
              >
                {step.label}
              </span>
            </button>

            {/* Connector line */}
            {idx < STEPPER_STEPS.length - 1 && (
              <div className="flex-1 mx-2 h-px min-w-2">
                <div
                  className={cn(
                    'h-full rounded-full',
                    isComplete ? 'bg-success' : 'bg-line'
                  )}
                />
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
